import React, { memo, useEffect, useMemo } from 'react';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';

import useOwnership from '@hooks/checkOwnership';
import Link from '@components/Link';
import ApplySection from '@components/SingleOffer/ApplySection';
import { Logo, CompanyName, Contact, EditLinkWrapper } from '@components/SingleOffer/OfferDetailsBox/styles';
import ListItem from '@components/ListItem';
import { Card, ListDotted, OfferTypeLabel } from '@styles/globalStyles';
import routes from '@utils/routes';
import { EMPLOYER_TABS } from '@constants/profileTabs';
import { POSITION } from '@constants/offerFilters';
import Mask from '@assets/images/mask-group.svg';

interface OfferDetailsBoxProps {
  offer: Offer;
}

function OfferDetailsBox({ offer }: OfferDetailsBoxProps) {
  const { t } = useTranslation(['offers', 'common']);
  const { isOwner } = useOwnership(offer.employerProfile.id, 'employer');

  const employerUrl = useMemo(() => routes.employerDetails(offer.employerProfile.slug), [offer.employerProfile.slug]);

  if (!offer) {
    return null;
  }

  return (
    <Card className="offer-details">
      {isOwner ? (
        <EditLinkWrapper>
          <Link href={`${routes.profile}?tab=${EMPLOYER_TABS.OFFER}&slug=${offer.slug}`}>{t('Edit the data')}</Link>
        </EditLinkWrapper>
      ) : null}

      <OfferTypeLabel type={offer.practiceType}>{t(offer.practiceType)}</OfferTypeLabel>

      <CompanyName>
        <Link href={employerUrl}>{offer.employerProfile.name}</Link>
      </CompanyName>

      <ListDotted>
        <ListItem>
          {t('Paid')}:&nbsp;<strong>{offer.paid ? t('Yes') : t('No')}</strong>
        </ListItem>

        <ListItem render={!!offer.salary}>
          {offer.salary ? (
            <>
              {t('Salary')}:&nbsp;
              <strong>
                {parseFloat(offer.salary.toString()).toLocaleString('en')}
                {offer.salaryMax ? ` - ${parseFloat(offer.salaryMax.toString()).toLocaleString('en')}` : ''}{' '}
                {offer.currency || 'PLN'}
              </strong>
            </>
          ) : null}
        </ListItem>

        <ListItem render={!!offer.companyDepartment}>
          {t('Company Department')}:&nbsp;<strong>{offer.companyDepartment}</strong>
        </ListItem>
        <ListItem render={!!offer.companyPosition}>
          {t('Company Position')}:&nbsp;<strong>{t(POSITION[offer.companyPosition], { ns: 'common' })}</strong>
        </ListItem>
        <ListItem render={!!offer.city}>
          {t('City')}:&nbsp;<strong>{offer.city}</strong>
        </ListItem>
        <ListItem render={!!offer.startWork && offer.startWork !== '1970-01-01'}>
          {t('Start date')}:&nbsp;<strong>{offer.startWork}</strong>
        </ListItem>
        <ListItem render={!!offer.stopWork && offer.stopWork !== '1970-01-01'}>
          {t('End date')}:&nbsp;<strong>{offer.stopWork}</strong>
        </ListItem>
      </ListDotted>

      {offer.employerProfile?.logo?.desktop ? (
        <Logo>
          <Image src={offer.employerProfile.logo.desktop} width="100%" height="100%" objectFit="contain" />
        </Logo>
      ) : (
        <Logo>
          <Image src={Mask} width="100%" height="100%" objectFit="contain" />
        </Logo>
      )}

      <Contact>
        <strong>{t('Contact')} :</strong>
        <ul>
          {offer.employerProfile?.website && (
            <li>
              <a
                href={
                  offer.employerProfile.website.match(/^http(s)?:\/\//)
                    ? offer.employerProfile.website
                    : `https://${offer.employerProfile.website}`
                }
                target="_blank"
                rel="noreferrer"
              >
                {offer.employerProfile.website}
              </a>
            </li>
          )}
          <li>
            {offer.employerProfile.street} {offer.employerProfile.streetNo}
          </li>
          <li>
            {offer.employerProfile.postCode} {offer.employerProfile.city}
          </li>
          <li>{offer.employerProfile.phoneNumber}</li>
        </ul>
      </Contact>

      <ApplySection offer={offer} />
    </Card>
  );
}

export default memo(OfferDetailsBox);
